import React, { Dispatch, SetStateAction, useState } from "react"
import tw, { styled } from "twin.macro"
import { useTranslation } from "next-i18next"
import ReactGA from "react-ga"
import { Button, ButtonTheme } from "../../formElements/button"
import { useGetEmailAvailableLazyQuery, useSendVerifyEmailOtpMutation } from "../../../graphql/generated/graphql"
import { AuthenticatonPage } from ".."
import { Input, InputType } from "../../formElements/input"
import { JoinStep } from "."
import "react-phone-input-2/lib/style.css"
import { validateEmailAvailability } from "../../../utils/validations"
import { getDiscordLoginUrl } from "../../../utils/discord"
import Link from "next/link"
import { PhoneForm } from "./phoneForm"
import { useDebounce } from "../../../utils/hooks/useDebounce"

interface ActiveTabProps {
  isActive: boolean
}
const Tab = styled.div(({ isActive }: ActiveTabProps) => [
  tw`pb-2 text-base-2 cursor-pointer font-sans font-bold relative hover:(text-dark-purple)`,
  tw`after:(absolute -bottom-2 -right-8 -left-8 bg-base-2 h-1 rounded-full)`,
  isActive && tw`text-dark-purple after:(bg-dark-purple z-10)`,
])

export enum TabState {
  Email = "email",
  Phone = "phone",
}
interface TabProps {
  activeTab: TabState
  setActiveTab: Dispatch<SetStateAction<TabState>>
}
export const Tabs = (props: TabProps) => {
  const { activeTab, setActiveTab } = props
  const { t } = useTranslation("authentication")
  return (
    <div data-testid="tabs" tw="flex flex-row space-x-16 justify-center items-center pt-6 pb-4">
      <Tab
        isActive={activeTab === TabState.Email}
        onClick={() => setActiveTab(TabState.Email)}
        data-testid={`tab-${TabState.Email.toLowerCase()}`}
      >
        {t("join.input_email_label")}
      </Tab>
      <Tab
        isActive={activeTab === TabState.Phone}
        onClick={() => setActiveTab(TabState.Phone)}
        data-testid={`tab-${TabState.Phone.toLowerCase()}`}
      >
        {t("join.input_phone_label")}
      </Tab>
    </div>
  )
}

interface MagicSignUpProp {
  setPage: Dispatch<SetStateAction<AuthenticatonPage>>
  setJoinStep: Dispatch<SetStateAction<JoinStep>>
  setSignUpEmail: Dispatch<SetStateAction<string>>
  setSignUpPhoneNumber: Dispatch<SetStateAction<string>>
  setSignUpPhoneCountryCode: Dispatch<SetStateAction<number>>
  isPage?: boolean
}
export const MagicSignUp = ({
  setPage,
  setJoinStep,
  setSignUpEmail,
  setSignUpPhoneNumber,
  setSignUpPhoneCountryCode,
  isPage,
}: MagicSignUpProp) => {
  const { t } = useTranslation("authentication")
  const [email, setEmail] = useState("")
  const [activeTab, setActiveTab] = useState<TabState>(TabState.Phone)
  const isEmail = activeTab == TabState.Email
  const [isValid, setValid] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [checkEmailAvailable, { loading: emailAvailableLoading }] = useGetEmailAvailableLazyQuery()
  const [sendVerifyEmailOtp] = useSendVerifyEmailOtpMutation()

  const validateEmailDebounce = useDebounce(validateEmailAvailability, 1000)
  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const inputValue = value.trim().toLowerCase()
    setValid(false)
    setErrorMessage("")
    setEmail(inputValue)
    validateEmailDebounce({ email: inputValue, setValid, setErrorMessage, checkEmailAvailable })
  }

  const submit = async (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault()
    if (process.env.NEXT_PUBLIC_ENV === "production" && typeof window !== "undefined") {
      ReactGA.event({
        category: "User",
        action: "get started",
      })
    }
    await sendVerifyEmailOtp({ variables: { email } })
    setSignUpEmail(email)
    setJoinStep(JoinStep.VerifyEmail)
  }

  const emailForm = (
    <form id="signupForm" tw="relative">
      <Input
        label={t("join.input_email_label")}
        placeholder={t("join.input_email_label")}
        name="email"
        inputValue={email}
        handleChange={handleEmail}
        loading={emailAvailableLoading}
        invalid={!!errorMessage}
        helpText={errorMessage}
        tabIndex={1}
        type={InputType.Email}
        data-testid="email-input"
      />
      <p tw="text-md font-bold text-center pb-[19px]">{t("join.info_email")}</p>
      <Button
        dataTestId="signup-button"
        action={t("join.next")}
        twStyle={tw`w-full`}
        onClickAsync={submit}
        isDisabled={!isValid}
      />
    </form>
  )

  // Note: We are explicitly setting the width to 260px when isPage
  return (
    <div
      tw="z-0 h-full w-full text-base-6 top-0 bottom-0 flex flex-col space-y-6"
      css={[isPage && tw`mobile-lg:(w-[260px])`]}
    >
      <>
        <h3 tw="title-3">{t("join.sign_up")}</h3>
        <div tw="space-y-4">
          <div>
            <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
            {isEmail ? emailForm : <PhoneForm {...{ setJoinStep, setSignUpPhoneNumber, setSignUpPhoneCountryCode }} />}
          </div>
          <div tw="space-y-4">
            <Link prefetch={false} passHref={true} href={getDiscordLoginUrl()}>
              <a>
                <Button
                  action={t("login.continue_discord")}
                  twStyle={tw`w-full`}
                  theme={ButtonTheme.Secondary}
                  tabIndex={4}
                />
              </a>
            </Link>
          </div>
          <p tw="text-center text-sm font-bold pt-6">
            <span>{t("join.login")} </span>
            <a tw="cursor-pointer" data-testid="switch-login" onClick={() => setPage(AuthenticatonPage.Login)}>
              {t("login.login")}
            </a>
          </p>
        </div>
      </>
    </div>
  )
}
